/**
 * Remote reports (GET /comptes-rendus).
 */
import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from '@/services/api-client';
import {
  mapReportDetail,
  type ReportDetailApi,
  type ReportListParams,
} from '@/types/remote/report.types';

/** Scalar `GET /comptes-rendus` query keys (arrays are appended separately). */
const REPORT_LIST_SCALAR_KEYS = [
  'page',
  'pageSize',
  'sessionType',
  'compteRenduStatus',
  'scoringStatus',
  'periodFrom',
  'periodTo',
  'studentUserId',
  'tutorUserId',
  'animatorUserId',
  'programId',
  'search',
  'sessionId',
  'schoolYearId',
] as const satisfies readonly (keyof ReportListParams)[];

/** Append list filters to a `URLSearchParams` (repeats `managedByIds`). */
function appendReportListParams(sp: URLSearchParams, params: ReportListParams): void {
  for (const key of REPORT_LIST_SCALAR_KEYS) {
    const value = params[key];
    if (value) sp.append(key, String(value));
  }
  params.managedByIds?.forEach((id) => sp.append('managedByIds', id));
}

/** `GET /comptes-rendus` — paginated remote reports. */
export const listReports = (params?: ReportListParams) => {
  const sp = new URLSearchParams();
  if (params) appendReportListParams(sp, params);
  const qs = sp.toString();
  const url = qs ? `/comptes-rendus?${qs}` : '/comptes-rendus';
  return apiClient.get(url).then((r) => r.data);
};

/** `GET /comptes-rendus/:id` */
export const getReport = (id: string) =>
  apiClient.get(`/comptes-rendus/${id}`).then((r) => mapReportDetail(r.data as ReportDetailApi));

/** `GET /comptes-rendus/by-session/:sessionId` */
export const getReportBySession = (sessionId: string) =>
  apiClient
    .get(`/comptes-rendus/by-session/${sessionId}`)
    .then((r) => (r.data == null ? null : mapReportDetail(r.data as ReportDetailApi)));

/** `PATCH /comptes-rendus/:id/compte-rendu` */
export const patchReport = (id: string, fields: Record<string, unknown>) =>
  apiClient.patch(`/comptes-rendus/${id}/compte-rendu`, { fields }).then((r) => r.data);

/** `PATCH /comptes-rendus/:id/validate` */
export const validateReport = (id: string) =>
  apiClient.patch(`/comptes-rendus/${id}/validate`).then((r) => r.data);

/** Same filters as {@link listReports}, without page/pageSize. Downloads the xlsx. */
export async function exportReports(
  params?: Omit<ReportListParams, 'page' | 'pageSize'>,
): Promise<void> {
  const sp = new URLSearchParams();
  if (params) appendReportListParams(sp, params);
  const qs = sp.toString();
  const url = qs ? `/comptes-rendus/export?${qs}` : '/comptes-rendus/export';
  const { data } = await apiClient.get<Blob>(url, { responseType: 'blob' });
  const blob = new Blob([data], { type: XLSX_MIME });
  const objectUrl = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `comptes-rendus-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(objectUrl);
}
